<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Etapas</span>
          <h2 class="workspace-title">Gestion comercial de etapas</h2>
          <p class="workspace-copy">Define precios, stock, visibilidad y reglas de acceso desde una sola vista operativa.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button workspace-button--primary" type="button" @click="openCreatePanel">
            <i class="fas fa-plus"></i>
            <span>Nueva etapa</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total etapas</small>
          <strong>{{ tickets.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activas</small>
          <strong>{{ activeStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Ocultas</small>
          <strong>{{ hiddenStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Modo dominante</small>
          <strong>{{ modeHeadline }}</strong>
        </article>
      </div>
    </article>

    <article v-if="editorOpen" class="glass-surface workspace-panel workspace-editor-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Editor</span>
          <h2 class="workspace-title">{{ editingTicketId ? 'Editar etapa' : 'Nueva etapa' }}</h2>
          <p class="workspace-copy">Los campos clave quedan visibles arriba. Lo menos frecuente vive dentro de opciones avanzadas.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="resetForm">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <form class="workspace-form workspace-form--two" @submit.prevent="submitStage">
        <label class="workspace-field">
          <span>Nombre</span>
          <input v-model="form.name" class="workspace-input" type="text" placeholder="General Early Bird" required />
        </label>

        <label class="workspace-field">
          <span>Precio</span>
          <input v-model.number="form.price" class="workspace-input" type="number" min="0" step="0.01" required />
        </label>

        <label class="workspace-field">
          <span>Stock</span>
          <input v-model.number="form.initial_quantity" class="workspace-input" type="number" min="1" required />
        </label>

        <label class="workspace-field">
          <span>Máximo por compra</span>
          <input v-model.number="form.max_quantity" class="workspace-input" type="number" min="1" required />
        </label>

        <label class="workspace-field workspace-field--wide">
          <span>Descripción</span>
          <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="3" placeholder="Qué incluye y para quién aplica esta etapa"></textarea>
        </label>

        <label class="workspace-field workspace-field--wide">
          <span>Modo de códigos</span>
          <select v-model.number="form.code_mode" class="workspace-input workspace-select">
            <option :value="0">Sin códigos</option>
            <option :value="1">Aplica a todos los códigos</option>
            <option :value="2">Aplica solo a códigos seleccionados</option>
          </select>
        </label>

        <label v-if="form.code_mode === 2" class="workspace-field workspace-field--wide">
          <span>Códigos seleccionados</span>
          <select v-model="form.access_code_ids" class="workspace-input workspace-select workspace-select--multi" multiple>
            <option v-for="eventCode in activeCodes" :key="eventCode.id" :value="eventCode.id">{{ eventCode.code }} - {{ eventCode.label || 'Sin etiqueta' }}</option>
          </select>
          <small class="workspace-field__note">Una etapa puede compartir códigos con otras etapas sin perder su configuración de visibilidad.</small>
        </label>

        <div class="workspace-check-grid workspace-field--wide">
          <label class="workspace-inline-check">
            <input v-model="form.status" type="checkbox" />
            <span>Etapa activa</span>
          </label>
          <label class="workspace-inline-check">
            <input v-model="form.web_experience" type="checkbox" />
            <span>Visible en web</span>
          </label>
          <label class="workspace-inline-check">
            <input v-model="form.is_courtesy" type="checkbox" />
            <span>Cortesía</span>
          </label>
        </div>

        <div class="workspace-field workspace-field--wide workspace-advanced-block">
          <button class="workspace-chip-button workspace-chip-button--toggle" type="button" @click="advancedOpen = !advancedOpen">
            <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            <span>Opciones avanzadas</span>
          </button>

          <div v-if="advancedOpen" class="workspace-advanced-grid">
            <label class="workspace-field">
              <span>Entradas por unidad</span>
              <input v-model.number="form.quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field">
              <span>Tipo</span>
              <select v-model.number="form.type" class="workspace-input workspace-select">
                <option :value="1">Normal</option>
                <option :value="2">Discount</option>
                <option :value="3">Free</option>
                <option :value="4">Physical</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Categoría</span>
              <select v-model.number="form.category" class="workspace-input workspace-select">
                <option :value="1">FB / IG</option>
                <option :value="2">Tiktok</option>
                <option :value="3">Mail</option>
                <option :value="4">Ambassador</option>
                <option :value="5">Page</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Disponible desde</span>
              <input v-model="form.sales_date" class="workspace-input" type="datetime-local" />
            </label>

            <label class="workspace-field">
              <span>Disponible hasta</span>
              <input v-model="form.expiration_date" class="workspace-input" type="datetime-local" />
            </label>

            <div class="workspace-field workspace-field--wide workspace-limit-card">
              <div class="workspace-limit-card__header">
                <div>
                  <span class="workspace-limit-card__title">Límite por código en esta etapa</span>
                  <small class="workspace-field__note">Sirve para decidir cuántas veces puede entrar el mismo código a esta etapa. La home pública no consume este límite.</small>
                </div>
                <label class="workspace-inline-check workspace-inline-check--compact">
                  <input v-model="codeLimitEnabled" type="checkbox" :disabled="!codeUsageLimitApplies" />
                  <span>Limitar usos</span>
                </label>
              </div>

              <div class="workspace-limit-card__body">
                <label class="workspace-field workspace-field--limit-inline">
                  <span>Máximo de usos por código</span>
                <input
                  v-model.number="form.max_code_uses"
                  class="workspace-input"
                  type="number"
                  min="1"
                  :disabled="!codeUsageLimitApplies || !codeLimitEnabled"
                  placeholder="Ej: 3 usos"
                />
                </label>
                <p class="workspace-summary-line workspace-summary-line--compact">{{ codeLimitSummary }}</p>
              </div>
            </div>

            <p class="workspace-summary-line">{{ comboSummary }}</p>
          </div>
        </div>

        <div class="workspace-actions workspace-field--wide">
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : editingTicketId ? 'Actualizar etapa' : 'Crear etapa' }}</span>
          </button>
          <button class="workspace-button" type="button" @click="resetForm">
            <i class="fas fa-rotate-left"></i>
            <span>{{ editingTicketId ? 'Cancelar edición' : 'Cancelar' }}</span>
          </button>
        </div>
      </form>
    </article>

    <article v-if="fieldPanelOpen" class="glass-surface workspace-panel workspace-editor-panel guest-field-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Campos extra</span>
          <h2 class="workspace-title">{{ fieldPanelTicket ? `Datos para ${fieldPanelTicket.name}` : 'Campos de asistentes' }}</h2>
          <p class="workspace-copy">Crea campos personalizados para el evento, activalos por etapa y mira la salida antes de guardar.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="closeFieldPanel">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <div v-if="fieldPanelLoading" class="workspace-empty">Cargando configuracion de campos...</div>

      <div v-else class="guest-field-panel__layout">
        <div class="guest-field-panel__column guest-field-panel__column--catalog">
          <section class="guest-field-panel__card">
            <div class="workspace-panel__header workspace-panel__header--spread">
              <div>
                <span class="workspace-kicker">Catalogo</span>
                <h3 class="workspace-title">Campos reutilizables del evento</h3>
              </div>

              <button v-if="fieldEditorMode === 'edit'" class="workspace-chip-button" type="button" @click="resetFieldEditor">
                <i class="fas fa-rotate-left"></i>
                <span>Nuevo</span>
              </button>
            </div>

            <form class="workspace-form workspace-form--two" @submit.prevent="submitFieldCatalog">
              <label class="workspace-field workspace-field--wide">
                <span>Etiqueta</span>
                <input v-model.trim="fieldDraft.label" class="workspace-input" type="text" placeholder="Documento, empresa, talla camiseta..." required>
              </label>

              <label class="workspace-field">
                <span>Tipo</span>
                <select v-model="fieldDraft.field_type" class="workspace-input workspace-select">
                  <option value="field">Texto corto</option>
                  <option value="multiple">Seleccion unica</option>
                </select>
              </label>

              <label class="workspace-field">
                <span>Estado</span>
                <select v-model="fieldDraft.is_active" class="workspace-input workspace-select">
                  <option :value="true">Activo</option>
                  <option :value="false">Inactivo</option>
                </select>
              </label>

              <label class="workspace-field workspace-field--wide">
                <span>Ayuda breve</span>
                <input v-model.trim="fieldDraft.help_text" class="workspace-input" type="text" placeholder="Explica que debe escribir o elegir la persona.">
              </label>

              <label v-if="fieldDraft.field_type === 'multiple'" class="workspace-field workspace-field--wide">
                <span>Opciones</span>
                <textarea v-model="fieldDraft.options_raw" class="workspace-input workspace-input--textarea" rows="4" placeholder="Una opcion por linea"></textarea>
                <small class="workspace-field__note">Ejemplo: XS, S, M, L o Casa, Oficina, Otro.</small>
              </label>

              <div class="workspace-actions workspace-field--wide">
                <button class="workspace-button workspace-button--primary" type="submit" :disabled="fieldPanelSaving">
                  <i :class="fieldEditorMode === 'edit' ? 'fas fa-save' : 'fas fa-plus'"></i>
                  <span>{{ fieldPanelSaving ? 'Guardando...' : fieldEditorMode === 'edit' ? 'Actualizar campo' : 'Crear campo' }}</span>
                </button>
                <button v-if="fieldEditorMode === 'edit'" class="workspace-button" type="button" @click="resetFieldEditor">
                  <i class="fas fa-ban"></i>
                  <span>Cancelar</span>
                </button>
              </div>
            </form>
          </section>

          <section class="guest-field-panel__card">
            <div class="workspace-panel__header">
              <div>
                <span class="workspace-kicker">Biblioteca</span>
                <h3 class="workspace-title">Campos disponibles</h3>
              </div>
            </div>

            <div v-if="!fieldCatalog.length" class="workspace-empty">Todavia no creaste campos para este evento.</div>
            <div v-else class="guest-field-panel__catalog-list">
              <article v-for="field in fieldCatalog" :key="field.id" class="guest-field-card">
                <div>
                  <div class="guest-field-card__title-row">
                    <strong>{{ field.label }}</strong>
                    <span class="workspace-badge">{{ fieldTypeLabel(field.field_type) }}</span>
                  </div>
                  <small class="guest-field-card__meta">{{ field.assigned_tickets_count || 0 }} etapa(s)</small>
                  <p v-if="field.help_text" class="guest-field-card__copy">{{ field.help_text }}</p>
                  <p v-if="field.field_type === 'multiple' && field.options && field.options.length" class="guest-field-card__copy">{{ field.options.join(' / ') }}</p>
                </div>

                <div class="workspace-inline-actions">
                  <button v-if="!isFieldAssigned(field.id)" class="workspace-chip-button" type="button" @click="assignFieldToStage(field)">
                    <i class="fas fa-plus"></i>
                    <span>Agregar</span>
                  </button>
                  <button v-else class="workspace-chip-button" type="button" @click="removeAssignedField(field.id)">
                    <i class="fas fa-link-slash"></i>
                    <span>Quitar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="editFieldCatalog(field)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="deleteFieldCatalog(field)">
                    <i class="fas fa-trash-alt"></i>
                    <span>Eliminar</span>
                  </button>
                </div>
              </article>
            </div>
          </section>
        </div>

        <div class="guest-field-panel__column">
          <section class="guest-field-panel__card">
            <div class="workspace-panel__header workspace-panel__header--spread">
              <div>
                <span class="workspace-kicker">Etapa</span>
                <h3 class="workspace-title">Campos activos en esta etapa</h3>
              </div>

              <span class="workspace-badge">Base fija: Nombre + Apellido</span>
            </div>

            <div v-if="!fieldAssignments.length" class="workspace-empty">Esta etapa solo pedira nombre y apellido hasta que agregues campos.</div>

            <div v-else class="guest-field-panel__assigned-list">
              <article v-for="(assignment, index) in sortedFieldAssignments" :key="assignment.field_id" class="guest-stage-field-card">
                <div class="guest-stage-field-card__copy">
                  <div class="guest-field-card__title-row">
                    <strong>{{ assignment.label }}</strong>
                    <span class="workspace-badge">{{ fieldTypeLabel(assignment.field_type) }}</span>
                  </div>
                  <small>{{ assignment.help_text || 'Campo personalizado listo para checkout y reportes.' }}</small>
                </div>

                <div class="guest-stage-field-card__controls">
                  <label class="workspace-inline-check workspace-inline-check--compact">
                    <input :checked="assignment.is_required" type="checkbox" @change="toggleAssignedRequired(assignment.field_id, $event.target.checked)">
                    <span>Obligatorio</span>
                  </label>

                  <label class="workspace-field workspace-field--limit-inline guest-stage-field-card__order">
                    <span>Orden</span>
                    <input class="workspace-input" type="number" min="0" :value="assignment.sort_order" @input="updateAssignedSortOrder(assignment.field_id, $event.target.value)">
                  </label>

                  <button class="workspace-chip-button" type="button" @click="moveAssignedField(assignment.field_id, -1)" :disabled="index === 0">
                    <i class="fas fa-arrow-up"></i>
                    <span>Subir</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="moveAssignedField(assignment.field_id, 1)" :disabled="index === sortedFieldAssignments.length - 1">
                    <i class="fas fa-arrow-down"></i>
                    <span>Bajar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="removeAssignedField(assignment.field_id)">
                    <i class="fas fa-times"></i>
                    <span>Quitar</span>
                  </button>
                </div>
              </article>
            </div>

            <div class="workspace-actions">
              <button class="workspace-button workspace-button--primary" type="button" :disabled="fieldPanelSaving || !fieldPanelTicket" @click="saveStageGuestFields">
                <i class="fas fa-save"></i>
                <span>{{ fieldPanelSaving ? 'Guardando...' : 'Guardar configuracion de etapa' }}</span>
              </button>
            </div>
          </section>

          <section class="guest-field-panel__card guest-field-panel__card--preview">
            <div class="workspace-panel__header">
              <div>
                <span class="workspace-kicker">Preview</span>
                <h3 class="workspace-title">Asi se vera en checkout</h3>
              </div>
            </div>

            <div class="guest-field-preview">
              <article class="guest-field-preview__field">
                <div class="guest-field-preview__row">
                  <strong>Nombre</strong>
                  <span class="guest-field-preview__badge is-required">Obligatorio</span>
                </div>
                <div class="guest-field-preview__input"></div>
              </article>

              <article class="guest-field-preview__field">
                <div class="guest-field-preview__row">
                  <strong>Apellido</strong>
                  <span class="guest-field-preview__badge is-required">Obligatorio</span>
                </div>
                <div class="guest-field-preview__input"></div>
              </article>

              <article v-for="assignment in sortedFieldAssignments" :key="`preview-${assignment.field_id}`" class="guest-field-preview__field">
                <div class="guest-field-preview__row">
                  <strong>{{ assignment.label }}</strong>
                  <span :class="['guest-field-preview__badge', assignment.is_required ? 'is-required' : 'is-optional']">{{ assignment.is_required ? 'Obligatorio' : 'Opcional' }}</span>
                </div>
                <small v-if="assignment.help_text" class="guest-field-preview__copy">{{ assignment.help_text }}</small>
                <div v-if="assignment.field_type === 'multiple'" class="guest-field-preview__chips">
                  <span v-for="option in assignment.options || []" :key="option" class="guest-field-preview__chip">{{ option }}</span>
                </div>
                <div v-else class="guest-field-preview__input"></div>
              </article>
            </div>
          </section>
        </div>
      </div>
    </article>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Inventario</span>
          <h2 class="workspace-title">Etapas actuales</h2>
        </div>
      </div>

      <div v-if="loading && !tickets.length" class="workspace-empty">Cargando etapas...</div>
      <div v-else-if="!tickets.length" class="workspace-empty">Todavía no hay etapas creadas. Crea la primera para empezar a vender o segmentar accesos.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table">
          <thead>
            <tr>
              <th>Etapa</th>
              <th>Precio</th>
              <th>Stock</th>
              <th>Max</th>
              <th>Combo</th>
              <th>Visibilidad</th>
              <th>Códigos</th>
              <th>Campos extra</th>
              <th>Límite por código</th>
              <th>Status</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in tickets" :key="ticket.id">
              <td>
                <div class="workspace-table-title">{{ ticket.name }}</div>
                <p class="workspace-table-subcopy">{{ ticket.description || 'Sin descripción.' }}</p>
              </td>
              <td>${{ formatMoney(ticket.price) }}</td>
              <td>{{ ticket.initial_quantity }}</td>
              <td>{{ ticket.max_quantity }}</td>
              <td>{{ comboLabel(ticket) }}</td>
              <td>{{ stageVisibilityLabel(ticket) }}</td>
              <td>
                <span v-if="Number(ticket.code_mode) === 1">Todos</span>
                <span v-else-if="Number(ticket.code_mode) === 0">Sin códigos</span>
                <span v-else>{{ accessCodeSummary(ticket) }}</span>
              </td>
              <td>{{ guestFieldSummary(ticket) }}</td>
              <td>{{ codeLimitLabel(ticket) }}</td>
              <td>
                <span :class="['workspace-status-pill', ticket.status ? 'is-active' : 'is-off']">{{ ticket.status ? 'Activa' : 'Oculta' }}</span>
              </td>
              <td>
                <div class="workspace-inline-actions">
                  <button class="workspace-chip-button" type="button" @click="openFieldPanel(ticket)">
                    <i class="fas fa-sliders-h"></i>
                    <span>Campos</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="deleteStage(ticket)">
                    <i class="fas fa-trash-alt"></i>
                    <span>{{ ticket.status ? 'Archivar' : 'Eliminar' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'

const defaultForm = () => ({
  name: '',
  description: '',
  initial_quantity: 100,
  price: 0,
  status: true,
  web_experience: true,
  is_courtesy: false,
  type: 1,
  category: 5,
  sales_date: '',
  expiration_date: '',
  require_ig: false,
  quantity: 1,
  max_quantity: 5,
  max_code_uses: null,
  code_mode: 0,
  access_code_ids: [],
})

const defaultFieldDraft = () => ({
  id: null,
  label: '',
  field_type: 'field',
  options_raw: '',
  help_text: '',
  is_active: true,
})

export default {
  name: 'StagesView',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      tickets: [],
      codes: [],
      editingTicketId: null,
      codeLimitEnabled: false,
      successMessage: '',
      errorMessage: '',
      editorOpen: false,
      advancedOpen: false,
      fieldPanelOpen: false,
      fieldPanelLoading: false,
      fieldPanelSaving: false,
      fieldPanelTicket: null,
      fieldCatalog: [],
      fieldAssignments: [],
      fieldEditorMode: 'create',
      fieldDraft: defaultFieldDraft(),
      form: defaultForm(),
    }
  },

  computed: {
    activeStages() {
      return this.tickets.filter((ticket) => ticket.status)
    },

    hiddenStages() {
      return this.tickets.filter((ticket) => !ticket.status)
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    modeHeadline() {
      const selectedCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 2).length
      const allCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 1).length

      if (selectedCount > 0) {
        return 'Seleccionados'
      }

      if (allCount > 0) {
        return 'Todos'
      }

        return 'Público'
    },

    comboSummary() {
      const units = Number(this.form.quantity) || 1
      const max = Number(this.form.max_quantity) || 1
      const total = units * max
      return units > 1
        ? `Cada unidad entrega ${units} entradas. El maximo por compra permite hasta ${total} accesos en una sola orden.`
        : `Cada unidad entrega una entrada. El maximo por compra permite hasta ${max} accesos en una sola orden.`
    },

    codeUsageLimitApplies() {
      return !this.form.web_experience && Number(this.form.code_mode) !== 0
    },

    codeLimitSummary() {
      if (!this.codeUsageLimitApplies) {
        return 'No aplica aquí: esta etapa está visible en home o no depende de códigos.'
      }

      if (!this.codeLimitEnabled || !this.form.max_code_uses) {
        return 'Cada código puede entrar sin límite a esta etapa privada.'
      }

      return `Un mismo código podrá entrar hasta ${this.form.max_code_uses} veces a esta etapa.`
    },

    sortedFieldAssignments() {
      return [...this.fieldAssignments].sort((left, right) => {
        const leftOrder = Number(left.sort_order || 0)
        const rightOrder = Number(right.sort_order || 0)
        if (leftOrder === rightOrder) {
          return String(left.label || '').localeCompare(String(right.label || ''))
        }
        return leftOrder - rightOrder
      })
    },
  },

  created() {
    this.fetchAll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.closeFieldPanel()
        this.fetchAll()
      },
    },

    codeUsageLimitApplies(value) {
      if (!value) {
        this.codeLimitEnabled = false
        this.form.max_code_uses = null
      }
    },

    codeLimitEnabled(value) {
      if (!value) {
        this.form.max_code_uses = null
      }
    },
  },

  methods: {
    fetchAll() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      Promise.all([
        apiService(`/api/host/events/${this.event.id}/tickets/`),
        apiService(`/api/host/events/${this.event.id}/codes/`),
      ]).then(([tickets, codes]) => {
        this.tickets = Array.isArray(tickets) ? tickets : []
        this.codes = Array.isArray(codes) ? codes : []
      }).finally(() => {
        this.loading = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    openCreatePanel() {
      this.resetMessages()
      this.editingTicketId = null
      this.editorOpen = true
      this.advancedOpen = false
      this.codeLimitEnabled = false
      this.form = defaultForm()
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    formatInputDate(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      const pad = (number) => `${number}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    stageVisibilityLabel(ticket) {
      if (ticket.web_experience && Number(ticket.code_mode) === 0) {
        return 'Web publica'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 1) {
        return 'Web + todos los códigos'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 2) {
        return 'Web + códigos elegidos'
      }
      if (Number(ticket.code_mode) === 1 || Number(ticket.code_mode) === 2) {
        return 'Privada por código'
      }
      return 'Oculta'
    },

    comboLabel(ticket) {
      const quantity = Number(ticket.quantity) || 1
      return quantity > 1 ? `${quantity}x por unidad` : 'Simple'
    },

    accessCodeSummary(ticket) {
      const count = Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.length : 0
      if (!count) {
        return 'Sin seleccion'
      }
      if (count === 1) {
        return ticket.access_codes_detail[0].code
      }
      return `${count} códigos`
    },

    codeLimitLabel(ticket) {
      if (ticket.web_experience || Number(ticket.code_mode) === 0) {
        return 'No aplica'
      }

      return ticket.max_code_uses ? `${ticket.max_code_uses} usos máx.` : 'Sin límite'
    },

    guestFieldSummary(ticket) {
      const count = Array.isArray(ticket.guest_fields) ? ticket.guest_fields.length : 0
      if (!count) {
        return 'Base fija'
      }
      if (count === 1) {
        return ticket.guest_fields[0].label || '1 campo'
      }
      return `${count} campos`
    },

    fieldTypeLabel(value) {
      if (value === 'multiple') {
        return 'Seleccion unica'
      }
      return 'Texto corto'
    },

    normalizeOptionsRaw(value) {
      return String(value || '')
        .split(/\r?\n/)
        .map((option) => option.trim())
        .filter((option, index, collection) => option !== '' && collection.indexOf(option) === index)
    },

    resetFieldEditor() {
      this.fieldEditorMode = 'create'
      this.fieldDraft = defaultFieldDraft()
    },

    isFieldAssigned(fieldId) {
      return this.fieldAssignments.some((assignment) => Number(assignment.field_id) === Number(fieldId))
    },

    syncAssignmentOrder() {
      this.fieldAssignments = this.sortedFieldAssignments.map((assignment, index) => ({
        ...assignment,
        sort_order: index,
      }))
    },

    assignFieldToStage(field) {
      if (!field || this.isFieldAssigned(field.id)) {
        return
      }

      this.fieldAssignments = this.fieldAssignments.concat({
        field_id: field.id,
        label: field.label,
        slug: field.slug,
        field_type: field.field_type,
        options: Array.isArray(field.options) ? field.options : [],
        help_text: field.help_text || '',
        is_required: false,
        sort_order: this.fieldAssignments.length,
        is_active: true,
      })
      this.syncAssignmentOrder()
    },

    removeAssignedField(fieldId) {
      this.fieldAssignments = this.fieldAssignments.filter((assignment) => Number(assignment.field_id) !== Number(fieldId))
      this.syncAssignmentOrder()
    },

    toggleAssignedRequired(fieldId, checked) {
      this.fieldAssignments = this.fieldAssignments.map((assignment) => Number(assignment.field_id) === Number(fieldId)
        ? { ...assignment, is_required: Boolean(checked) }
        : assignment)
    },

    updateAssignedSortOrder(fieldId, value) {
      const parsedValue = Number.parseInt(value, 10)
      this.fieldAssignments = this.fieldAssignments.map((assignment) => Number(assignment.field_id) === Number(fieldId)
        ? { ...assignment, sort_order: Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue) }
        : assignment)
      this.syncAssignmentOrder()
    },

    moveAssignedField(fieldId, direction) {
      const ordered = this.sortedFieldAssignments
      const currentIndex = ordered.findIndex((assignment) => Number(assignment.field_id) === Number(fieldId))
      const nextIndex = currentIndex + direction
      if (currentIndex === -1 || nextIndex < 0 || nextIndex >= ordered.length) {
        return
      }

      const clone = [...ordered]
      const [movedItem] = clone.splice(currentIndex, 1)
      clone.splice(nextIndex, 0, movedItem)
      this.fieldAssignments = clone.map((assignment, index) => ({ ...assignment, sort_order: index }))
    },

    fetchFieldCatalog() {
      if (!this.event || !this.event.id) {
        return Promise.resolve([])
      }

      return apiService(`/api/host/events/${this.event.id}/guest-fields/`).then((data) => {
        this.fieldCatalog = Array.isArray(data) ? data : []
        this.$emit('event-updated', { guest_field_catalog: [...this.fieldCatalog] })
        return this.fieldCatalog
      })
    },

    openFieldPanel(ticket) {
      this.resetMessages()
      this.fieldPanelOpen = true
      this.fieldPanelLoading = true
      this.fieldPanelTicket = ticket
      this.resetFieldEditor()
      apiService(`/api/host/events/tickets/${ticket.id}/guest-fields/`).then((data) => {
        this.fieldCatalog = Array.isArray(data && data.catalog) ? data.catalog : []
        this.$emit('event-updated', { guest_field_catalog: [...this.fieldCatalog] })
        this.fieldAssignments = Array.isArray(data && data.items) ? data.items : []
      }).catch(() => {
        this.errorMessage = 'No pudimos cargar la configuracion de campos.'
      }).finally(() => {
        this.fieldPanelLoading = false
      })
    },

    closeFieldPanel() {
      this.fieldPanelOpen = false
      this.fieldPanelLoading = false
      this.fieldPanelSaving = false
      this.fieldPanelTicket = null
      this.fieldCatalog = []
      this.fieldAssignments = []
      this.resetFieldEditor()
    },

    editFieldCatalog(field) {
      this.fieldEditorMode = 'edit'
      this.fieldDraft = {
        id: field.id,
        label: field.label || '',
        field_type: field.field_type || 'field',
        options_raw: Array.isArray(field.options) ? field.options.join('\n') : '',
        help_text: field.help_text || '',
        is_active: field.is_active !== false,
      }
    },

    submitFieldCatalog() {
      if (!this.event || !this.event.id) {
        return
      }

      this.resetMessages()
      this.fieldPanelSaving = true
      const payload = {
        label: this.fieldDraft.label,
        field_type: this.fieldDraft.field_type,
        options: this.normalizeOptionsRaw(this.fieldDraft.options_raw),
        help_text: this.fieldDraft.help_text,
        is_active: Boolean(this.fieldDraft.is_active),
      }
      const isEditing = this.fieldEditorMode === 'edit' && this.fieldDraft.id
      const endpoint = isEditing
        ? `/api/host/events/${this.event.id}/guest-fields/${this.fieldDraft.id}/`
        : `/api/host/events/${this.event.id}/guest-fields/`

      apiService(endpoint, isEditing ? 'PATCH' : 'POST', payload).then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        return this.fetchFieldCatalog().then(() => {
          const latestField = this.fieldCatalog.find((field) => Number(field.id) === Number(data.id)) || data
          if (!isEditing && latestField) {
            this.assignFieldToStage(latestField)
          }
          this.successMessage = isEditing ? 'Campo actualizado.' : 'Campo creado y listo para usar.'
          this.resetFieldEditor()
        })
      }).catch(() => {
        this.errorMessage = 'No pudimos guardar el campo.'
      }).finally(() => {
        this.fieldPanelSaving = false
      })
    },

    deleteFieldCatalog(field) {
      if (!field || !window.confirm(`Eliminar ${field.label}? Esto lo quitara de todas las etapas.`)) {
        return
      }

      this.resetMessages()
      this.fieldPanelSaving = true
      apiService(`/api/host/events/${this.event.id}/guest-fields/${field.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.fieldAssignments = this.fieldAssignments.filter((assignment) => Number(assignment.field_id) !== Number(field.id))
        this.syncAssignmentOrder()
        this.resetFieldEditor()
        return this.fetchFieldCatalog().then(() => {
          this.successMessage = 'Campo eliminado.'
        })
      }).catch(() => {
        this.errorMessage = 'No pudimos eliminar el campo.'
      }).finally(() => {
        this.fieldPanelSaving = false
      })
    },

    updateTicketGuestFieldsLocally(ticketId, guestFields) {
      this.tickets = this.tickets.map((ticket) => Number(ticket.id) === Number(ticketId)
        ? { ...ticket, guest_fields: guestFields }
        : ticket)
      if (this.fieldPanelTicket && Number(this.fieldPanelTicket.id) === Number(ticketId)) {
        this.fieldPanelTicket = {
          ...this.fieldPanelTicket,
          guest_fields: guestFields,
        }
      }
    },

    saveStageGuestFields() {
      if (!this.fieldPanelTicket) {
        return
      }

      this.resetMessages()
      this.fieldPanelSaving = true
      const payload = {
        assignments: this.sortedFieldAssignments.map((assignment, index) => ({
          field_id: assignment.field_id,
          is_required: Boolean(assignment.is_required),
          sort_order: index,
        })),
      }

      apiService(`/api/host/events/tickets/${this.fieldPanelTicket.id}/guest-fields/`, 'PUT', payload).then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.fieldAssignments = Array.isArray(data && data.items) ? data.items : []
        this.updateTicketGuestFieldsLocally(this.fieldPanelTicket.id, this.fieldAssignments)
        return this.fetchFieldCatalog().then(() => {
          this.successMessage = 'Campos extra guardados para esta etapa.'
        })
      }).catch(() => {
        this.errorMessage = 'No pudimos guardar los campos de la etapa.'
      }).finally(() => {
        this.fieldPanelSaving = false
      })
    },

    normalizedPayload() {
      const selectedCode = this.codes.find((code) => code.id === Number(this.form.access_code_ids[0]))
      return {
        ...this.form,
        code_mode: Number(this.form.code_mode),
        type: Number(this.form.type),
        category: Number(this.form.category),
        initial_quantity: Number(this.form.initial_quantity),
        price: Number(this.form.price),
        quantity: Number(this.form.quantity),
        max_quantity: Number(this.form.max_quantity),
        max_code_uses: this.codeUsageLimitApplies && this.codeLimitEnabled && this.form.max_code_uses ? Number(this.form.max_code_uses) : null,
        web_experience: Boolean(this.form.web_experience),
        require_ig: false,
        sales_date: this.form.sales_date ? `${this.form.sales_date}:00` : null,
        expiration_date: this.form.expiration_date ? `${this.form.expiration_date}:00` : null,
        access_code_ids: Number(this.form.code_mode) === 2 ? this.form.access_code_ids : [],
        code: Number(this.form.code_mode) === 2 && selectedCode ? selectedCode.code : '',
      }
    },

    submitStage() {
      this.resetMessages()
      this.saving = true

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`

      apiService(endpoint, this.editingTicketId ? 'PATCH' : 'POST', this.normalizedPayload()).then((data) => {
        if (data && !data.err) {
          this.successMessage = this.editingTicketId ? 'Etapa actualizada.' : 'Etapa creada.'
          this.fetchAll()
          this.resetForm()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo guardar la etapa.'
      }).finally(() => {
        this.saving = false
      })
    },

    editStage(ticket) {
      this.resetMessages()
      this.editingTicketId = ticket.id
      this.editorOpen = true
      this.advancedOpen = true
      this.codeLimitEnabled = Boolean(ticket.max_code_uses)
      this.form = {
        name: ticket.name || '',
        description: ticket.description || '',
        initial_quantity: ticket.initial_quantity || 0,
        price: Number.parseFloat(ticket.price) || 0,
        status: Boolean(ticket.status),
        web_experience: Boolean(ticket.web_experience),
        is_courtesy: Boolean(ticket.is_courtesy),
        type: Number(ticket.type) || 1,
        category: Number(ticket.category) || 5,
        sales_date: this.formatInputDate(ticket.sales_date),
        expiration_date: this.formatInputDate(ticket.expiration_date),
        require_ig: Boolean(ticket.require_ig),
        quantity: Number(ticket.quantity) || 1,
        max_quantity: Number(ticket.max_quantity) || 5,
        max_code_uses: Number(ticket.max_code_uses) || null,
        code_mode: Number(ticket.code_mode) || 0,
        access_code_ids: Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.map((code) => code.id) : [],
      }
    },

    resetForm() {
      this.editingTicketId = null
      this.editorOpen = false
      this.advancedOpen = false
      this.codeLimitEnabled = false
      this.form = defaultForm()
    },

    deleteStage(ticket) {
      this.resetMessages()
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.successMessage = ticket.status ? 'Etapa archivada.' : 'Etapa eliminada.'
        if (this.editingTicketId === ticket.id) {
          this.resetForm()
        }
        if (this.fieldPanelTicket && Number(this.fieldPanelTicket.id) === Number(ticket.id)) {
          this.closeFieldPanel()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar la etapa.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-panel--hero {
  display: grid;
  gap: 18px;
}

.workspace-panel__header--spread {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-editor-panel {
  display: grid;
  gap: 18px;
}

.workspace-advanced-block {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.02);
}

.workspace-chip-button--toggle {
  justify-self: flex-start;
}

.workspace-advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.workspace-summary-line {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  color: var(--text-soft);
  background: rgba(203, 255, 232, 0.06);
  border: 1px solid rgba(203, 255, 232, 0.12);
  grid-column: 1 / -1;
}

.workspace-summary-line--compact {
  padding: 0;
  background: transparent;
  border: 0;
}

.workspace-limit-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(223, 214, 255, 0.08);
  grid-column: 1 / -1;
}

.workspace-limit-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-limit-card__title {
  display: block;
  margin-bottom: 6px;
  color: var(--text-main);
  font-weight: 700;
}

.workspace-limit-card__body {
  display: grid;
  gap: 10px;
}

.workspace-field--limit-inline {
  display: grid;
  gap: 8px;
}

.workspace-inline-check--compact {
  white-space: nowrap;
}

.workspace-table-shell {
  overflow: auto;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
  vertical-align: top;
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.workspace-table-title {
  color: var(--text-main);
  font-weight: 700;
}

.workspace-table-subcopy {
  margin: 6px 0 0;
  max-width: 340px;
}

.workspace-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.guest-field-panel__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 18px;
}

.guest-field-panel__column,
.guest-field-panel__card,
.guest-field-panel__catalog-list,
.guest-field-panel__assigned-list,
.guest-field-preview {
  display: grid;
  gap: 14px;
}

.guest-field-panel__card {
  padding: 18px;
  border-radius: 20px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.guest-field-card,
.guest-stage-field-card,
.guest-field-preview__field {
  display: grid;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(190, 224, 255, 0.12);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
}

.guest-field-card__title-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.guest-field-card__meta,
.guest-field-card__copy,
.guest-field-preview__copy {
  margin: 0;
  color: var(--text-soft);
}

.guest-stage-field-card__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: end;
}

.guest-stage-field-card__order {
  min-width: 90px;
}

.guest-field-preview__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.guest-field-preview__badge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.guest-field-preview__badge.is-required {
  color: #143f31;
  background: #baf4d9;
}

.guest-field-preview__badge.is-optional {
  color: #a7b9de;
  background: rgba(167, 185, 222, 0.12);
}

.guest-field-preview__input {
  height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(223, 214, 255, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.guest-field-preview__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.guest-field-preview__chip {
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(140, 243, 232, 0.24);
  background: rgba(140, 243, 232, 0.1);
  color: var(--text-main);
  font-weight: 600;
}

@media (max-width: 900px) {
  .workspace-panel__header--spread {
    display: grid;
  }

  .workspace-advanced-grid {
    grid-template-columns: 1fr;
  }

  .workspace-limit-card__header {
    display: grid;
  }

  .guest-field-panel__layout {
    grid-template-columns: 1fr;
  }

  .guest-stage-field-card__controls,
  .guest-field-preview__row,
  .guest-field-card__title-row {
    display: grid;
  }
}
</style>
